import { FlashbarProps } from '@cloudscape-design/components';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import React, { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import * as yup from 'yup';

import {
  GetWebsiteNewsDocument,
  GetWebsiteNewsQuery,
  useCreateWebsiteNewsMutation,
  WebsiteNews,
} from '../../../../generated/graphql';
import { ErrorText } from '../../../../lib/ErrorTexts';
import Form from './Form';
import { createDDBItem } from './lib/Helper';

type CreateProps = {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  notifyDeletedOrUpdated: (resource: FlashbarProps.MessageDefinition) => void;
  notifyInProgress: (resource?: FlashbarProps.MessageDefinition) => void;
  notifyFailed: (failedResource: FlashbarProps.MessageDefinition) => void;
};

export type FormValues = {
  title: string;
  text: string;
  is_active: boolean;
};

export const schema = yup
  .object({
    title: yup.string().required(ErrorText.Required),
    text: yup.string().required(ErrorText.Required),
    is_active: yup.boolean().required(ErrorText.Required),
  })
  .required();

export default function Create({
  visible,
  setVisible,
  notifyInProgress,
  notifyDeletedOrUpdated,
  notifyFailed,
}: CreateProps) {
  const [createWebsiteNews, { loading: createLoading }] =
    useCreateWebsiteNewsMutation();
  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {},
    resolver: yupResolver(schema),
  });
  const onSubmit = (data: any) => {
    const entry = createDDBItem(data);
    const messageId = uuidv4();
    notifyInProgress({ id: messageId, header: 'Eintrag wird erstellt...' });
    createWebsiteNews({
      variables: { input: entry },
      update: (cache, { data }) => {
        const addData = data?.createWebsiteNews;
        const existingData = cache.readQuery({
          query: GetWebsiteNewsDocument,
        }) as GetWebsiteNewsQuery;
        const oldData = existingData.listWebsiteNews?.items as WebsiteNews[];

        cache.writeQuery({
          query: GetWebsiteNewsDocument,
          data: {
            listWebsiteNews: { items: [...oldData, addData] },
          },
        });
      },
    })
      .then((data) => {
        const result = data.data?.createWebsiteNews;
        if (result) {
          notifyInProgress();
          notifyDeletedOrUpdated({
            id: messageId,
            type: 'success',
            content: `Eintrag mit dem Namen "${result.title}" wurde erstellt.`,
            header: 'Gespeichert',
            dismissible: true,
          });
          handleCancel();
        }
      })
      .catch((error) => {
        notifyInProgress();
        notifyFailed({
          id: messageId,
          content: JSON.stringify(error),
          header: 'Ein Fehler ist aufgetreten',
          dismissible: true,
        });
      });
  };

  const handleCancel = useCallback(() => {
    setVisible(false);
    methods.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [methods.reset, setVisible]);

  return (
    <FormProvider {...methods}>
      <Modal
        onDismiss={handleCancel}
        visible={visible}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCancel}>
                Abbrechen
              </Button>
              <Button
                variant="primary"
                disabled={
                  isEmpty(methods.formState.dirtyFields) || createLoading
                }
                formAction="submit"
                onClick={() => methods.handleSubmit(onSubmit)()}
              >
                Speichern
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={'Eintrag erstellen'}
      >
        <Form />
      </Modal>
    </FormProvider>
  );
}
